import { SESSION_SUMMARY_MAX_BODY_BYTES } from "./session-summary-contract.js";

/** State, locking, atomic persistence, and payload compaction for the emitted hook. */
export function buildSessionSummaryCollectorHookFragment(): string {
  return `
var STATE_SCHEMA_VERSION = "orgx-session-collector-state/v2";
// A burst of separate client hook processes can serialize behind file+directory
// fsync. Keep the wait finite, but long enough that correctness wins over
// dropping an observed event during realistic multi-process contention.
var STATE_LOCK_MAX_WAIT_MS = 5000;
var STATE_LOCK_ORPHAN_GRACE_MS = 2000;
var STATE_LOCK_POLL_MS = 2;
var STATE_LOCK_WAIT_ARRAY = new Int32Array(new SharedArrayBuffer(4));
var MAX_SESSION_SUMMARY_BODY_BYTES = ${SESSION_SUMMARY_MAX_BODY_BYTES};

export function statePath(sessionId, sourceClient, dir) {
  var root = dir ? dir : defaultStateDir();
  var safeClient = String(sourceClient || "unknown")
    .replace(/[^A-Za-z0-9._-]/g, "_")
    .slice(0, 24) || "unknown";
  var clientDigest = createHash("sha256")
    .update(String(sourceClient || "unknown"))
    .digest("hex")
    .slice(0, 16);
  var digest = createHash("sha256").update(String(sessionId)).digest("hex");
  return join(root, safeClient + "-" + clientDigest + "-" + digest + ".json");
}

export function legacyStatePath(sessionId, dir) {
  var root = dir ? dir : defaultStateDir();
  var safe = String(sessionId).replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 128);
  return join(root, safe + ".json");
}

function stateIdentity(sessionId, sourceClient) {
  return {
    source_client: sourceClient,
    session_id: sessionId,
    session_id_sha256: createHash("sha256").update(String(sessionId)).digest("hex"),
  };
}

export function emptyStateEnvelope(sessionId, sourceClient, cwd, nowIso) {
  return {
    schema_version: STATE_SCHEMA_VERSION,
    identity: stateIdentity(sessionId, sourceClient),
    conversation: emptyState(sessionId, sourceClient, cwd, nowIso, "conversation"),
    run: emptyState(sessionId, sourceClient, cwd, nowIso, "run", 1),
    next_run_ordinal: 2,
  };
}

function stateHasIdentity(state, sessionId, sourceClient) {
  return state &&
    typeof state === "object" &&
    state.session_id === sessionId &&
    state.source_client === sourceClient;
}

export function readState(path, sessionId, sourceClient) {
  try {
    var parsed = JSON.parse(readFileSync(path, "utf8"));
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    var identity = parsed.identity;
    var expectedDigest = createHash("sha256").update(String(sessionId)).digest("hex");
    if (
      parsed.schema_version !== STATE_SCHEMA_VERSION ||
      !identity ||
      identity.session_id !== sessionId ||
      identity.source_client !== sourceClient ||
      identity.session_id_sha256 !== expectedDigest ||
      !stateHasIdentity(parsed.conversation, sessionId, sourceClient) ||
      (parsed.run !== null && !stateHasIdentity(parsed.run, sessionId, sourceClient))
    ) {
      return null;
    }
    if (!Number.isInteger(parsed.next_run_ordinal) || parsed.next_run_ordinal < 1) {
      parsed.next_run_ordinal = 1;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

export function readStateWithLegacy(
  path,
  oldPath,
  sessionId,
  sourceClient
) {
  var current = readState(path, sessionId, sourceClient);
  if (current) return { envelope: current, legacy_path: null };
  try {
    var legacy = JSON.parse(readFileSync(oldPath, "utf8"));
    if (!stateHasIdentity(legacy, sessionId, sourceClient)) return null;
    ensureExecutionState(legacy);
    legacy.state_scope = "conversation";
    delete legacy.run_ordinal;
    var run = JSON.parse(JSON.stringify(legacy));
    run.state_scope = "run";
    run.run_ordinal = 1;
    return {
      envelope: {
        schema_version: STATE_SCHEMA_VERSION,
        identity: stateIdentity(sessionId, sourceClient),
        conversation: legacy,
        run: run,
        next_run_ordinal: 2,
      },
      legacy_path: oldPath,
    };
  } catch (e) {
    return null;
  }
}

export function writeState(path, state) {
  var temporary = null;
  var descriptor = null;
  try {
    var root = dirname(path);
    mkdirSync(root, { recursive: true, mode: 0o700 });
    temporary = join(
      root,
      "." + basename(path) + ".tmp-" + process.pid + "-" +
        Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10)
    );
    descriptor = openSync(temporary, "wx", 0o600);
    writeSync(descriptor, JSON.stringify(state), undefined, "utf8");
    fsyncSync(descriptor);
    closeSync(descriptor);
    descriptor = null;
    renameSync(temporary, path);
    temporary = null;
    var directoryDescriptor = openSync(root, "r");
    try {
      fsyncSync(directoryDescriptor);
    } finally {
      closeSync(directoryDescriptor);
    }
    return true;
  } catch (e) {
    if (descriptor !== null) {
      try { closeSync(descriptor); } catch (ignored) {}
    }
    if (temporary) {
      try { rmSync(temporary, { force: true }); } catch (ignored) {}
    }
    return false;
  }
}

export function removeState(path) {
  try {
    var root = dirname(path);
    rmSync(path, { force: true });
    var directoryDescriptor = openSync(root, "r");
    try {
      fsyncSync(directoryDescriptor);
    } finally {
      closeSync(directoryDescriptor);
    }
    return true;
  } catch (e) {
    return false;
  }
}

function pauseForStateLock() {
  try {
    Atomics.wait(STATE_LOCK_WAIT_ARRAY, 0, 0, STATE_LOCK_POLL_MS);
  } catch (e) {
    // Atomics.wait is available on supported Node runtimes. If a host disables
    // it, the bounded retry loop still terminates without blocking the client.
  }
}

function stateLockToken() {
  return process.pid + "-" + Date.now().toString(36) + "-" +
    Math.random().toString(36).slice(2, 12);
}

function stateLockOwnerName(token) {
  return "owner-" + token + ".json";
}

function stateLockOwnerPath(lockPath, token) {
  return join(lockPath, stateLockOwnerName(token));
}

function readStateLockOwner(path) {
  try {
    var current = JSON.parse(readFileSync(path, "utf8"));
    var pid = Number(current && current.pid);
    var token = current && current.token;
    if (!Number.isInteger(pid) || pid <= 0) return null;
    if (typeof token !== "string" || !token) return null;
    if (basename(path) !== stateLockOwnerName(token)) return null;
    return { pid: pid, token: token };
  } catch (e) {
    return null;
  }
}

function stateLockOwnerAlive(owner) {
  try {
    process.kill(owner.pid, 0);
    return true;
  } catch (e) {
    return Boolean(e && e.code === "EPERM");
  }
}

function stateLockMarkerNames(lockPath) {
  try {
    return readdirSync(lockPath).filter(function (name) {
      return /^owner-[A-Za-z0-9-]+[.]json$/.test(name);
    });
  } catch (e) {
    return [];
  }
}

export function staleStateLockOwners(lockPath) {
  var stale = [];
  var names = stateLockMarkerNames(lockPath);
  for (var i = 0; i < names.length; i++) {
    var markerPath = join(lockPath, names[i]);
    try {
      var ageMs = Date.now() - statSync(markerPath).mtimeMs;
      if (ageMs <= STATE_LOCK_ORPHAN_GRACE_MS) continue;
      var owner = readStateLockOwner(markerPath);
      if (owner && stateLockOwnerAlive(owner)) continue;
      stale.push({
        marker_path: markerPath,
        marker_name: names[i],
        token: owner ? owner.token : null,
      });
    } catch (e) {
      // The marker changed while it was inspected. Leave it for a fresh pass.
    }
  }
  return stale;
}

export function removeStaleStateLockOwner(lockPath, claim) {
  try {
    if (!claim || dirname(claim.marker_path) !== lockPath) return false;
    if (basename(claim.marker_path) !== claim.marker_name) return false;
    var current = readStateLockOwner(claim.marker_path);
    if (claim.token !== null && (!current || current.token !== claim.token)) {
      return false;
    }
    if (claim.token === null && current) return false;
    rmSync(claim.marker_path, { force: true });
  } catch (e) {
    return false;
  }
  try { rmdirSync(lockPath); } catch (ignored) {}
  return true;
}

function staleLegacyStateLock(path) {
  try {
    var stats = statSync(path);
    if (stats.isDirectory()) return false;
    if (Date.now() - stats.mtimeMs <= STATE_LOCK_ORPHAN_GRACE_MS) return false;
    var current = JSON.parse(readFileSync(path, "utf8"));
    var pid = Number(current && current.pid);
    if (!Number.isInteger(pid) || pid <= 0) return true;
    return !stateLockOwnerAlive({ pid: pid });
  } catch (e) {
    try {
      var fallbackStats = statSync(path);
      return !fallbackStats.isDirectory() &&
        Date.now() - fallbackStats.mtimeMs > STATE_LOCK_ORPHAN_GRACE_MS;
    } catch (ignored) {
      return false;
    }
  }
}

function prepareStateLockCandidate(lockPath, token) {
  var candidatePath = lockPath + ".candidate-" + token;
  var descriptor = null;
  try {
    mkdirSync(candidatePath, { mode: 0o700 });
    var markerPath = stateLockOwnerPath(candidatePath, token);
    descriptor = openSync(markerPath, "wx", 0o600);
    writeSync(descriptor, JSON.stringify({
      pid: process.pid,
      token: token,
      acquired_at: new Date().toISOString(),
    }));
    fsyncSync(descriptor);
    closeSync(descriptor);
    descriptor = null;
    return { path: candidatePath, token: token };
  } catch (e) {
    if (descriptor !== null) {
      try { closeSync(descriptor); } catch (ignored) {}
    }
    try { rmSync(candidatePath, { recursive: true, force: true }); } catch (ignored) {}
    return null;
  }
}

function removeEmptyStateLockDirectory(lockPath) {
  try {
    rmdirSync(lockPath);
    return true;
  } catch (e) {
    return false;
  }
}

function recoverStaleStateLock(lockPath) {
  try {
    if (!statSync(lockPath).isDirectory()) {
      if (!staleLegacyStateLock(lockPath)) return false;
      // New owners always use directories. A racing replacement therefore
      // makes this non-recursive removal fail instead of deleting its lock.
      try { rmSync(lockPath, { force: true }); } catch (ignored) {}
      return true;
    }
  } catch (e) {
    return false;
  }

  var staleOwners = staleStateLockOwners(lockPath);
  var recovered = false;
  for (var i = 0; i < staleOwners.length; i++) {
    recovered = removeStaleStateLockOwner(lockPath, staleOwners[i]) || recovered;
  }
  if (stateLockMarkerNames(lockPath).length === 0) {
    recovered = removeEmptyStateLockDirectory(lockPath) || recovered;
  }
  return recovered;
}

function acquireStateLock(stateFile) {
  var lockPath = stateFile + ".lock";
  var deadline = Date.now() + STATE_LOCK_MAX_WAIT_MS;
  try {
    mkdirSync(dirname(stateFile), { recursive: true, mode: 0o700 });
  } catch (e) {
    return null;
  }
  var token = stateLockToken();
  var candidate = prepareStateLockCandidate(lockPath, token);
  if (!candidate) return null;
  while (Date.now() <= deadline) {
    try {
      renameSync(candidate.path, lockPath);
      return {
        path: lockPath,
        token: token,
        marker_path: stateLockOwnerPath(lockPath, token),
      };
    } catch (e) {
      recoverStaleStateLock(lockPath);
      pauseForStateLock();
    }
  }
  try { rmSync(candidate.path, { recursive: true, force: true }); } catch (ignored) {}
  return null;
}

function releaseStateLock(lock) {
  try {
    var current = readStateLockOwner(lock.marker_path);
    if (!current || current.token !== lock.token) return false;
    rmSync(lock.marker_path, { force: true });
  } catch (e) {
    return false;
  }
  try { rmdirSync(lock.path); } catch (ignored) {}
  return true;
}

export function withStateLock(stateFile, operation) {
  var lock = acquireStateLock(stateFile);
  if (!lock) return { acquired: false };
  try {
    return { acquired: true, value: operation() };
  } finally {
    releaseStateLock(lock);
  }
}

export function ensureRunState(envelope, cwd, nowIso) {
  if (envelope.run) return envelope.run;
  var ordinal = Number.isInteger(envelope.next_run_ordinal) && envelope.next_run_ordinal > 0
    ? envelope.next_run_ordinal
    : 1;
  envelope.run = emptyState(
    envelope.identity.session_id,
    envelope.identity.source_client,
    cwd,
    nowIso,
    "run",
    ordinal
  );
  envelope.next_run_ordinal = ordinal + 1;
  return envelope.run;
}

function captureByteLength(capture) {
  return Buffer.byteLength(JSON.stringify(capture), "utf8");
}

function compactionState(summary, originalBytes, maxBytes) {
  if (!summary.metadata || typeof summary.metadata !== "object") summary.metadata = {};
  if (!summary.metadata.compaction || typeof summary.metadata.compaction !== "object") {
    summary.metadata.compaction = {
      schema_version: "orgx-session-summary-compaction/v1",
      original_bytes: originalBytes,
      cap_bytes: maxBytes,
      action_observations_omitted: 0,
      intent_observations_omitted: 0,
      work_contexts_omitted: 0,
      cwd_values_omitted: 0,
      lineage_values_omitted: 0,
      work_episodes_omitted: 0,
      execution_observations_omitted: 0,
    };
  }
  return summary.metadata.compaction;
}

function omitOneAction(summary, compaction) {
  var execution = summary.metadata && summary.metadata.execution;
  var actions = execution && Array.isArray(execution.actions) ? execution.actions : [];
  if (actions.length === 0) return false;
  var index = actions.length > 9 ? 8 : actions.length - 1;
  var removed = actions.splice(index, 1)[0];
  execution.actions_omitted = Math.max(0, Number(execution.actions_omitted) || 0) + 1;
  var aggregate = execution.completed_action_aggregate;
  if (aggregate && typeof aggregate === "object") {
    var completedIncluded = actions.filter(function (action) {
      return action && action.status !== "running";
    }).length;
    var completedOmitted = Math.max(0, Number(aggregate.details_omitted) || 0) +
      (removed && removed.status !== "running" ? 1 : 0);
    aggregate.details_included = completedIncluded;
    aggregate.details_omitted = completedOmitted;
    aggregate.detail_mode = completedOmitted > 0 ? "representative" : "complete";
    aggregate.detail_strategy = completedOmitted > 0 ? "first_8_last_24" : "all";
    if (!aggregate.consistency || typeof aggregate.consistency !== "object") {
      aggregate.consistency = {};
    }
    aggregate.consistency.detail_partition_matches_observed =
      completedIncluded + completedOmitted ===
      Math.max(0, Number(aggregate.actions_completed_observed) || 0);
    aggregate.capture_complete = aggregate.capture_complete === true &&
      aggregate.consistency.detail_partition_matches_observed === true;
    execution.action_capture_complete = execution.action_capture_complete === true &&
      aggregate.capture_complete === true;
  } else {
    execution.action_capture_complete = false;
  }
  compaction.action_observations_omitted += 1;
  return true;
}

function omitOneIntent(summary, compaction) {
  var episode = summary.metadata && summary.metadata.work_episode;
  var intent = episode && episode.intent;
  var observations = intent && Array.isArray(intent.observations) ? intent.observations : [];
  if (observations.length === 0) return false;
  var index = observations.length > 2 ? 1 : observations.length - 1;
  observations.splice(index, 1);
  intent.observations_omitted = Math.max(0, Number(intent.observations_omitted) || 0) + 1;
  intent.capture_complete = false;
  if (episode.privacy && observations.length === 0) {
    episode.privacy.profile = "metadata_only";
    episode.privacy.content_included = false;
  }
  compaction.intent_observations_omitted += 1;
  return true;
}

function omitWorkContext(summary, compaction) {
  if (!summary.metadata || !summary.metadata.work_context) return false;
  delete summary.metadata.work_context;
  compaction.work_contexts_omitted += 1;
  var execution = summary.metadata.execution;
  if (execution && Array.isArray(execution.unavailable_fields)) {
    var unavailable = ["intent", "authority_decision", "artifact_refs", "cost"];
    for (var i = 0; i < unavailable.length; i++) {
      if (execution.unavailable_fields.indexOf(unavailable[i]) < 0) {
        execution.unavailable_fields.push(unavailable[i]);
      }
    }
  }
  return true;
}

function omitCwd(summary, compaction) {
  if (summary.cwd === undefined || summary.cwd === null) return false;
  delete summary.cwd;
  compaction.cwd_values_omitted += 1;
  return true;
}

function omitLineage(summary, compaction) {
  var episode = summary.metadata && summary.metadata.work_episode;
  if (!episode || !episode.lineage) return false;
  var count = Object.keys(episode.lineage).filter(function (key) { return key !== "provenance"; }).length;
  delete episode.lineage;
  compaction.lineage_values_omitted += count;
  return true;
}

function omitWorkEpisode(summary, compaction) {
  if (!summary.metadata || !summary.metadata.work_episode) return false;
  delete summary.metadata.work_episode;
  compaction.work_episodes_omitted += 1;
  return true;
}

function omitExecution(summary, compaction) {
  if (!summary.metadata || !summary.metadata.execution) return false;
  delete summary.metadata.execution;
  compaction.execution_observations_omitted += 1;
  return true;
}

export function compactSummaryCapture(capture, maxBytes) {
  var limit = Number.isInteger(maxBytes) && maxBytes > 0
    ? maxBytes
    : MAX_SESSION_SUMMARY_BODY_BYTES;
  var output;
  try {
    output = JSON.parse(JSON.stringify(capture));
  } catch (e) {
    return null;
  }
  var originalBytes = captureByteLength(output);
  if (originalBytes <= limit) return output;
  var summary = output.session;
  if (!summary || typeof summary !== "object") return null;
  var compaction = compactionState(summary, originalBytes, limit);
  while (captureByteLength(output) > limit) {
    var changed = omitOneAction(summary, compaction) ||
      omitOneIntent(summary, compaction) ||
      omitWorkContext(summary, compaction) ||
      omitCwd(summary, compaction) ||
      omitLineage(summary, compaction) ||
      omitWorkEpisode(summary, compaction) ||
      omitExecution(summary, compaction);
    if (!changed) return null;
  }
  return output;
}

`;
}
